import type {
  HomeNutritionSummary,
  HomeProgressSummary,
  HomeSourceContext,
  HomeTrainingSummary,
  NutritionHomeSource,
  ProgressHomeSource,
  TrainingHomeSource,
} from '../ports/home-sources.ts';

import type {
  NutritionResult,
} from '../domain/nutrition.ts';

import type {
  ProgressResult,
} from '../domain/progress.ts';

import type {
  TrainingErrorCode,
  TrainingResult,
} from '../domain/training.ts';

export interface HomeDashboardView {
  localDate: string;

  training:
    TrainingResult<HomeTrainingSummary>;

  nutrition:
    NutritionResult<HomeNutritionSummary>;

  progress:
    ProgressResult<HomeProgressSummary>;
}

function sameContext(
  a: HomeSourceContext,
  b: HomeSourceContext,
) {
  return (
    a.datasetId === b.datasetId &&
    a.controlEpoch === b.controlEpoch
  );
}

function transitionFailure(
  localDate: string,
  code: Extract<
    TrainingErrorCode,
    'DATASET_STALE' | 'RESTORE_IN_PROGRESS'
  >,
): HomeDashboardView {
  const failure = {
    ok: false as const,
    code,
  };

  return {
    localDate,
    training: failure,
    nutrition: failure,
    progress: failure,
  };
}

export class HomeDashboard {
  constructor(
    private readonly training:
      TrainingHomeSource,

    private readonly nutrition:
      NutritionHomeSource,

    private readonly progress:
      ProgressHomeSource,
  ) {}

  async read(
    localDate: string,
  ): Promise<HomeDashboardView> {
    const [
      training,
      nutrition,
      progress,
    ] = await Promise.all([
      this.training
        .readTrainingHome(
          localDate,
        ),

      this.nutrition
        .readNutritionHome(
          localDate,
        ),

      this.progress
        .readProgressHome(
          localDate,
        ),
    ]);

    let transitionCode:
      | 'DATASET_STALE'
      | 'RESTORE_IN_PROGRESS'
      | undefined;

    for (const result of [
      training,
      nutrition,
      progress,
    ]) {
      if (
        !result.ok &&
        (
          result.code === 'DATASET_STALE' ||
          result.code === 'RESTORE_IN_PROGRESS'
        )
      ) {
        transitionCode = result.code;
        break;
      }
    }

    if (transitionCode) {
      return transitionFailure(
        localDate,
        transitionCode,
      );
    }

    const contexts: HomeSourceContext[] = [];

    if (training.ok) {
      contexts.push(
        training.value.context,
      );
    }

    if (nutrition.ok) {
      contexts.push(
        nutrition.value.context,
      );
    }

    if (progress.ok) {
      contexts.push(
        progress.value.context,
      );
    }

    if (
      contexts.length > 1 &&
      contexts.some(
        context =>
          !sameContext(
            contexts[0]!,
            context,
          ),
      )
    ) {
      return transitionFailure(
        localDate,
        'DATASET_STALE',
      );
    }

    return {
      localDate,
      training,
      nutrition,
      progress,
    };
  }
}
